import { createFileRoute, notFound } from "@tanstack/react-router";
import { getDeCalculatorBySlug, getEnCalculatorSlugForDeSlug, getDeCalculatorSlugForEnSlug } from "@/lib/de-calculators-catalog";
import { getEsCalculatorSlugForEnSlug } from "@/lib/es-calculators-catalog";
import { getFrCalculatorSlugForEnSlug } from "@/lib/fr-calculators-catalog";
import { getPtBrCalculatorSlugForEnSlug } from "@/lib/pt-br-calculators-catalog";
import { CALCULATOR_CONTENT } from "@/lib/calculator-content";
import { CalculatorShell } from "@/components/calculators/CalculatorShell";
import { CalculatorRenderer } from "@/components/calculators/CalculatorRenderer";

export const Route = createFileRoute("/de/calculators/$slug")({
  loader: ({ params }) => {
    const deCalc = getDeCalculatorBySlug(params.slug);
    if (!deCalc) throw notFound();
    const enSlug = getEnCalculatorSlugForDeSlug(deCalc.slug);
    const content = CALCULATOR_CONTENT[enSlug];
    if (!content) throw notFound();
    return { deCalc, enSlug, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { deCalc, enSlug } = loaderData;
    const currentCanonical = `https://picamini.online/de/calculators/${deCalc.slug}`;
    const enUrl = `https://picamini.online/calculators/${enSlug}`;
    const esUrl = `https://picamini.online/es/calculators/${getEsCalculatorSlugForEnSlug(enSlug)}`;
    const frUrl = `https://picamini.online/fr/calculators/${getFrCalculatorSlugForEnSlug(enSlug)}`;
    const ptUrl = `https://picamini.online/pt-br/calculators/${getPtBrCalculatorSlugForEnSlug(enSlug)}`;

    return {
      meta: [
        { title: `${deCalc.name} | Picamini.online` },
        { name: "description", content: deCalc.description },
        { name: "keywords", content: deCalc.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: deCalc.name },
        { property: "og:description", content: deCalc.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: currentCanonical },
        { property: "og:locale", content: "de_DE" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${deCalc.name} — Kostenloser Rechner auf Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: deCalc.name },
        { name: "twitter:description", content: deCalc.description },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [
        { rel: "canonical", href: currentCanonical },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "de", href: currentCanonical },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "fr", href: frUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: ptUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": deCalc.name,
            "url": currentCanonical,
            "description": deCalc.description,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "inLanguage": "de",
            "browserRequirements": "Erfordert JavaScript. Erfordert HTML5.",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://picamini.online/de/" },
              { "@type": "ListItem", "position": 2, "name": "Rechner", "item": "https://picamini.online/de/calculators" },
              { "@type": "ListItem", "position": 3, "name": deCalc.name, "item": currentCanonical },
            ],
          }),
        },
      ],
    };
  },
  component: SingleDeCalculatorPage,
});

function SingleDeCalculatorPage() {
  const { deCalc, enSlug, content } = Route.useLoaderData();

  const calcMetaAdapter = {
    slug: deCalc.slug,
    name: deCalc.name,
    tagline: deCalc.tagline,
    description: deCalc.description,
    category: deCalc.category as any,
    icon: deCalc.icon,
    featured: deCalc.featured,
    keywords: deCalc.keywords,
  };

  return (
    <CalculatorShell calc={calcMetaAdapter} content={content} lang="de">
      <CalculatorRenderer slug={enSlug} />
    </CalculatorShell>
  );
}
